import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import KPIGroups from "./KPIGroups";
import { getKpiTypeColor } from "../../../utils/kpiColors";

const KPIWorks = () => {
  const dispatch = useDispatch();
  const { myWorks, kpiProjects } = useSelector((state) => state.kpi);

  const [viewMode, setViewMode] = useState('works');
  const [statusList, setStatusList] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [allWorksList, setAllWorksList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch.kpi.getMyWorks({ status: "all", limit: 500 });
    dispatch.kpi.getKPIProjects({ hideLoading: true });
    dispatch.trashandStatus.getStatusData().then(res => {
      if (res && res.data) {
        setStatusList(res.data);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'all' && myWorks?.works && Array.isArray(myWorks.works)) {
      setAllWorksList(myWorks.works);
    }
  }, [myWorks, activeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    dispatch.kpi.getMyWorks({ status: tabId, limit: 500 });
  };

  const worksData = myWorks?.works || [];
  const summary = myWorks?.summary || {};
  const worksCount = summary.total !== undefined ? summary.total : worksData.length;
  const projectsCount = (kpiProjects || []).length || 0;

  if (viewMode === 'groups') {
    return (
      <KPIGroups 
        onSwitchView={(mode) => setViewMode(mode)} 
        currentViewMode={viewMode} 
        worksCount={worksCount} 
        projectsCount={projectsCount} 
      />
    );
  }

  // Helpers to safely extract status name and status ID from work item
  const getStatusNameString = (row) => {
    if (!row) return "";
    let name = "";
    if (typeof row.status === "string" && row.status) {
      name = row.status;
    } else if (typeof row.status_name === "string" && row.status_name) {
      name = row.status_name;
    } else if (typeof row.status_name === "object" && row.status_name) {
      name = row.status_name.status_name || row.status_name.task_status || "";
    } else if (typeof row.status === "object" && row.status) {
      name = row.status.status_name || row.status.task_status || "";
    } else if (typeof row.task_status === "string" && row.task_status) {
      name = row.task_status;
    } else if (typeof row.task_status === "object" && row.task_status) {
      name = row.task_status.task_status || row.task_status.status_name || "";
    } else if (typeof row.review_status === "string") {
      name = row.review_status;
    }
    return (name || "").toLowerCase().trim();
  };

  const getStatusId = (row) => {
    if (!row) return null;
    if (typeof row.task_status === "object" && row.task_status?._id) return String(row.task_status._id);
    if (typeof row.task_status === "string") return String(row.task_status);
    if (typeof row.status_name === "object" && row.status_name?._id) return String(row.status_name._id);
    if (row.status_id) return String(row.status_id);
    return null;
  };

  const isWorkMatchingTab = (w, tabId) => {
    if (!w) return false;
    const tabKey = String(tabId).toLowerCase().replace(/[\s-_]/g, '');
    if (tabKey === 'all') return true;

    const statusName = getStatusNameString(w);
    const statusClean = statusName.replace(/[\s-_]/g, '');
    const statusObjId = getStatusId(w);

    if (tabKey === 'review') {
      return statusClean.includes('review') || statusClean.includes('submitted') || (statusObjId && String(statusObjId) === String(tabId));
    }
    if (tabKey === 'completed') {
      return statusClean.includes('completed') || statusClean.includes('finished') || statusClean.includes('done');
    }
    if (tabKey === 'delayed') {
      return statusClean.includes('delayed') || statusClean.includes('overdue') || (w.due_date && new Date(w.due_date) < new Date() && !statusClean.includes('completed') && !statusClean.includes('finished') && !statusClean.includes('done'));
    }
    if (tabKey === 'active') {
      return statusClean.includes('active') || statusClean.includes('new') || statusClean.includes('progress') || statusClean.includes('ongoing') || statusClean.includes('todo') || (!statusClean.includes('completed') && !statusClean.includes('finished') && !statusClean.includes('done') && !statusClean.includes('delete'));
    }
    if (tabKey === 'todo') {
      return statusClean.includes('todo') || statusClean === 'todo' || statusClean === 'to-do' || statusClean === 'to_do';
    }

    if (statusObjId && String(statusObjId) === String(tabId)) return true;
    if (statusClean === tabKey || statusName === String(tabId).toLowerCase()) return true;

    return false;
  };

  // Construct status filter tabs based on summary or countSource dataset
  const countSource = allWorksList.length > 0 ? allWorksList : worksData;
  const baseTabs = [
    { id: 'all', label: 'All', count: summary?.total !== undefined ? summary.total : countSource.length },
    { id: 'active', label: 'Active', count: summary?.active !== undefined ? summary.active : countSource.filter(w => isWorkMatchingTab(w, 'active')).length },
    { id: 'review', label: 'Review', count: summary?.review !== undefined ? summary.review : countSource.filter(w => isWorkMatchingTab(w, 'review')).length },
    { id: 'delayed', label: 'Delayed', count: summary?.delayed !== undefined ? summary.delayed : countSource.filter(w => isWorkMatchingTab(w, 'delayed')).length },
    { id: 'completed', label: 'Completed', count: summary?.completed !== undefined ? summary.completed : countSource.filter(w => isWorkMatchingTab(w, 'completed')).length },
    { id: 'todo', label: 'ToDo', count: summary?.todo !== undefined ? summary.todo : countSource.filter(w => isWorkMatchingTab(w, 'todo')).length },
  ];

  const getOwnerColor = (ownerStr) => {
    const s = (ownerStr || "").toUpperCase();
    if (s.includes("SR")) return "bg-emerald-700";
    if (s.includes("JD")) return "bg-blue-700";
    if (s.includes("AF")) return "bg-amber-700";
    if (s.includes("LS")) return "bg-indigo-700";
    const colors = ["bg-emerald-700", "bg-blue-700", "bg-amber-700", "bg-indigo-700", "bg-rose-700", "bg-slate-700"];
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash += s.charCodeAt(i);
    return colors[hash % colors.length];
  };

  const getStatusBadgeStyle = (statusLabel) => {
    const s = (statusLabel || "").toLowerCase().replace(/_/g, " ").replace(/-/g, " ");

    if (s.includes("progress") || s.includes("active") || s.includes("ongoing") || s.includes("new") || s.includes("created")) {
      return { bg: "bg-[#f9f0e1]", text: "text-[#ab6919]", border: "border-[#f3e3ca]/80", dot: "bg-[#ab6919]" };
    }
    if (s.includes("review") || s.includes("submitted")) {
      return { bg: "bg-[#e3eaf7]", text: "text-[#2956bd]", border: "border-[#d4e0f4]/80", dot: "bg-[#2956bd]" };
    }
    if (s.includes("completed") || s.includes("done") || s.includes("finished")) {
      return { bg: "bg-[#e3f5ed]", text: "text-[#1b8354]", border: "border-[#d2efdf]/80", dot: "bg-[#1b8354]" };
    }
    if (s.includes("delayed") || s.includes("overdue") || s.includes("late")) {
      return { bg: "bg-[#f9e5e5]", text: "text-[#c73838]", border: "border-[#f3d3d3]/80", dot: "bg-[#c73838]" };
    }
    if (s.includes("waiting") || s.includes("dep") || s.includes("pending")) {
      return { bg: "bg-[#ece6fd]", text: "text-[#7055e8]", border: "border-[#dfd6fc]/80", dot: "bg-[#7055e8]" };
    }
    return { bg: "bg-[#eeece5]", text: "text-[#59554e]", border: "border-[#e2dfd7]/80", dot: "bg-[#59554e]" };
  };

  const formatStatusLabel = (label) => {
    if (!label || typeof label !== "string") return "";
    const clean = label.replace(/_/g, " ").replace(/-/g, " ").trim();
    if (!clean) return "";
    return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
  };

  const extraTabs = (statusList || [])
    .filter(s => s.task_status && !baseTabs.some(t => t.id === s.task_status.toLowerCase() || t.label.toLowerCase() === s.task_status.toLowerCase()))
    .map(s => {
      const tabId = s._id || s.task_status.toLowerCase();
      return {
        id: tabId,
        label: s.task_status,
        statusId: s._id,
        count: worksData.filter(w => isWorkMatchingTab(w, tabId) || getStatusId(w) === String(s._id) || getStatusNameString(w) === s.task_status.toLowerCase()).length
      };
    });

  const tabs = [...baseTabs, ...extraTabs];

  const filteredWorks = worksData.filter((w) => {
    if (!isWorkMatchingTab(w, activeTab)) return false;

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      const title = (w.title || w.task_name || '').toLowerCase();
      const proj = (w.project_name || w.project?.name || '').toLowerCase();
      const ownerName = (w.owner ? `${w.owner.first_name || ''} ${w.owner.last_name || ''}` : '').toLowerCase();
      const workId = (w.work_id || w.human_readable_id || w.task_id || w.id || '').toLowerCase();
      return title.includes(query) || proj.includes(query) || ownerName.includes(query) || workId.includes(query);
    }
    return true;
  });

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Work Management</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            All KPI-tracked activities you own or follow
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-start sm:justify-end">
          <Link
            to="/kpi/create"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 transition-colors shadow-xs !no-underline hover:!no-underline hover:!text-white"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create work
          </Link>
        </div>
      </div>

      {/* Top View Switcher Tabs (Works vs Projects) */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200/80 overflow-x-auto max-w-full scrollbar-none whitespace-nowrap">
        <button
          type="button"
          onClick={() => setViewMode('works')}
          className={`flex items-center gap-2.5 px-5 py-3 text-sm font-bold transition-all border-b-2 ${
            viewMode === 'works'
              ? 'border-slate-900 text-slate-900 bg-white'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={viewMode === 'works' ? 'text-slate-900' : 'text-slate-400'}>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
            <path d="m9 14 2 2 4-4"></path>
          </svg>
          <span>Works</span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${viewMode === 'works' ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500'}`}>
            {worksCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('groups')}
          className={`flex items-center gap-2.5 px-5 py-3 text-sm font-bold transition-all border-b-2 ${
            viewMode === 'groups'
              ? 'border-slate-900 text-slate-900 bg-white'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={viewMode === 'groups' ? 'text-slate-900' : 'text-slate-400'}>
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          </svg>
          <span>Projects</span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${viewMode === 'groups' ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500'}`}>
            {projectsCount}
          </span>
        </button>
      </div>

      {/* Status Filter Pills and Search */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative w-full md:w-80">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search works"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-full text-xs text-slate-900 bg-white shadow-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all"
          />
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs">
        <div className="overflow-x-auto rounded-xl">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-200/80 bg-[#f7f6f3]">
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-24">ID</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">TITLE</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-36">TYPE</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-44">STATUS</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-28">PRIORITY</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-28">DUE</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-20">KPI</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-24">OWNER</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredWorks.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-sm font-semibold text-slate-500">
                    No KPI works data found for this filter.
                  </td>
                </tr>
              ) : (
                filteredWorks.map((row, idx) => {
                  const isHighPriority = row.priority?.toLowerCase() === 'high';
                  const isLowPriority = row.priority?.toLowerCase() === 'low';
                  const isMedPriority = row.priority?.toLowerCase() === 'medium' || row.priority?.toLowerCase() === 'med';
                  const isDelayed = row.due_date && new Date(row.due_date) < new Date() && row.status?.toLowerCase() !== 'completed';

                  const statusLabel = (row.status_name && typeof row.status_name === "string")
                    ? row.status_name
                    : (typeof row.status_name === "object" && row.status_name?.status_name)
                    ? row.status_name.status_name
                    : (row.status && typeof row.status === "string")
                    ? row.status
                    : (typeof row.status === "object" && row.status?.status_name)
                    ? row.status.status_name
                    : (row.task_status && typeof row.task_status === "string")
                    ? row.task_status
                    : "In progress";

                  const ownerName = row.owner ? `${row.owner.first_name || ''} ${row.owner.last_name || ''}`.trim() : "";
                  const ownerInitials = row.owner
                    ? ((row.owner.first_name?.[0] || '') + (row.owner.last_name?.[0] || '')).toUpperCase()
                    : "?";
                  const ownerColorClass = getOwnerColor(ownerInitials);

                  const workHumanId = row.work_id || row.human_readable_id || (row.task_id ? `KPI-${row.task_id.slice(-3)}` : `KPI-${row.id?.slice(-3) || idx}`);
                  const typeName = row.kpi_type?.name || row.kpi_type_name || row.type || "Estimation";
                  const kpiPoints = row.kpi_points !== undefined ? row.kpi_points : (row.kpi_type?.score || 20);
                  const formattedPoints = kpiPoints >= 0 ? `+${kpiPoints}` : `${kpiPoints}`;

                  const statusStyle = getStatusBadgeStyle(statusLabel);

                  return (
                    <tr key={row.id || idx} className="hover:bg-slate-50/70 transition-colors group">
                      {/* ID */}
                      <td className="px-6 py-4">
                        <span className="text-xs font-semibold text-slate-400 font-mono">
                          {workHumanId}
                        </span>
                      </td>

                      {/* TITLE */}
                      <td className="px-6 py-4">
                        <Link to={`/kpi/works/${row.id}`} className="block max-w-xs">
                          <div className="text-sm font-bold text-slate-900 line-clamp-1">
                            {row.title}
                          </div>
                          <div className="text-xs font-medium text-slate-400 mt-0.5">
                            {row.project_name || row.project?.name || "Hexa Portal"}
                          </div>
                        </Link>
                      </td>

                      {/* TYPE */}
                      <td className="px-6 py-4">
                        {(() => {
                          const typeColor = getKpiTypeColor(typeName);
                          return (
                            <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${typeColor.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${typeColor.bg}`}></span>
                              <span>{typeName}</span>
                            </div>
                          );
                        })()}
                      </td>

                      {/* STATUS */}
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${statusStyle.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}></span>
                          <span>{formatStatusLabel(statusLabel)}</span>
                        </div>
                      </td>

                      {/* PRIORITY */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${isHighPriority ? 'bg-rose-600' : isMedPriority ? 'bg-amber-600' : isLowPriority ? 'bg-slate-400' : 'bg-slate-400'}`}></div>
                          <span className={`text-xs font-semibold ${isHighPriority ? 'text-rose-600' : isMedPriority ? 'text-amber-600' : isLowPriority ? 'text-slate-500' : 'text-slate-500'}`}>
                            {isHighPriority ? 'High' : isMedPriority ? 'Med' : isLowPriority ? 'Low' : 'Low'}
                          </span>
                        </div>
                      </td>

                      {/* DUE */}
                      <td className="px-6 py-4">
                        <span className={`text-xs font-bold ${isDelayed ? 'text-rose-600' : 'text-slate-700'}`}>
                          {row.due_date ? new Date(row.due_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : 'Jun 02'}
                        </span>
                      </td>

                      {/* KPI */}
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-slate-900">
                          {formattedPoints}
                        </span>
                      </td>

                      {/* OWNER / COLLABORATOR */}
                      <td className="px-6 py-4">
                        {ownerName ? (
                          <div className="inline-flex items-center gap-1.5 bg-[#f7f6f3] hover:bg-[#efeee7] transition-colors px-2.5 py-1 rounded-full border border-slate-200/80 shadow-2xs">
                            <div
                              className={`w-5 h-5 rounded-full ${ownerColorClass} text-white font-bold text-[9px] flex items-center justify-center shrink-0`}
                            >
                              {ownerInitials}
                            </div>
                            <span className="text-xs font-bold text-slate-900 truncate max-w-[130px]">
                              {ownerName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 font-medium">Unassigned</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
    </div>
  );
};

export default KPIWorks;
